import React, { useEffect, useState, useRef } from 'react';
import './ARRDashboard.css';

const DENTAL_API_URL = 'https://dentalexpressserver.azurewebsites.net';
// const DENTAL_API_URL = 'http://localhost:3001';

const fetchARR = async (endpoint) => {
  const token = localStorage.getItem('jwtToken');
  const url = `${DENTAL_API_URL}/api/arr/${endpoint}`;
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const err = new Error(`Failed to fetch ${endpoint}: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

// Payment status for this service month. Informational only: ARR counts a
// subscription in effect whether or not its invoice is paid.
const INVOICE_BADGES = {
  paid: ['invoice-paid', 'Paid'],
  open: ['invoice-unpaid', 'Open'],
  uncollectible: ['invoice-unpaid', 'Uncollectible'],
  none: ['invoice-pending', 'Not invoiced'],
};
function invoiceBadge(s) {
  const status = s.current_invoice_status || (s.current_invoice_paid ? 'paid' : 'none');
  const [cls, label] = INVOICE_BADGES[status] || INVOICE_BADGES.none;
  return <span className={cls}>{label}</span>;
}

const fmt = (n) => '$' + Math.round(n).toLocaleString();
const fmtPct = (n) => (n > 0 ? '+' : '') + n.toFixed(1) + '%';
const fmtChange = (n) => (n >= 0 ? '+' : '-') + '$' + Math.round(Math.abs(n)).toLocaleString();

function monthLabel(dateStr, partial) {
  const d = new Date(dateStr + 'T00:00:00');
  const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  return partial ? label + '*' : label;
}

export default function ARRDashboard() {
  const [current, setCurrent] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [churnData, setChurnData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('overview'); // 'overview' | 'subscriptions'
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [sortField, setSortField] = useState('mrr');
  const [sortDir, setSortDir] = useState('desc');
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    Promise.all([
      fetchARR('current'),
      fetchARR('monthly'),
      fetchARR('subscriptions'),
      fetchARR('churn'),
    ])
      .then(([c, m, s, ch]) => {
        setCurrent(c);
        setMonthly(m);
        setSubscriptions(s);
        setChurnData(ch || []);
        setLoading(false);
      })
      .catch((err) => {
        // ARR data is restricted to finance staff server-side; say so plainly
        // instead of surfacing a raw HTTP error.
        if (err.status === 403) setError('ARR data is restricted to finance staff. Ask Rohit if you need access.');
        else if (err.status === 401) setError('Your session has expired. Log out and back in to view ARR.');
        else setError(`Error: ${err.message}`);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!monthly.length || !chartRef.current) return;
    // Dynamically load Chart.js from CDN
    if (!window.Chart) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4';
      script.onload = () => renderChart();
      document.head.appendChild(script);
    } else {
      renderChart();
    }
    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthly]);

  function renderChart() {
    if (chartInstance.current) chartInstance.current.destroy();
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new window.Chart(ctx, {
      type: 'line',
      data: {
        labels: monthly.map((m) => monthLabel(m.date, m.partial)),
        datasets: [
          {
            label: 'ARR',
            data: monthly.map((m) => Math.round(m.arr)),
            borderColor: '#059669',
            backgroundColor: 'rgba(5,150,105,0.08)',
            fill: true,
            tension: 0.3,
            pointRadius: monthly.map((m) => (m.partial ? 6 : 4)),
            pointBackgroundColor: monthly.map((m) =>
              m.partial ? '#f59e0b' : '#059669'
            ),
            pointStyle: monthly.map((m) => (m.partial ? 'rectRot' : 'circle')),
            borderWidth: 2.5,
            segment: {
              borderDash: (ctx2) =>
                ctx2.p1DataIndex === monthly.length - 1 ? [4, 4] : [],
            },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1e293b',
            titleColor: '#e2e8f0',
            bodyColor: '#e2e8f0',
            padding: 12,
            cornerRadius: 8,
            callbacks: { label: (c) => 'ARR: ' + fmt(c.parsed.y) },
          },
        },
        scales: {
          y: {
            beginAtZero: false,
            ticks: {
              callback: (v) => '$' + (v / 1000).toFixed(0) + 'K',
              color: '#94a3b8',
            },
            grid: { color: '#f1f5f9' },
          },
          x: {
            ticks: { color: '#94a3b8', maxRotation: 45 },
            grid: { display: false },
          },
        },
      },
    });
  }

  if (loading) return <div className="arr-loading">Loading ARR data...</div>;
  if (error) return <div className="arr-error">{error}</div>;

  const rows = [...monthly].reverse();

  // Build churn lookup by month key (YYYY-MM)
  const churnByMonth = {};
  for (const c of churnData) {
    churnByMonth[c.month] = c;
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'customer_name' ? 'asc' : 'desc');
    }
  };

  const sortedSubs = [...subscriptions].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const payingSubs = sortedSubs.filter((s) => s.status !== 'trialing' && s.status !== 'paused');
  const trialSubs = sortedSubs.filter((s) => s.status === 'trialing');
  const pausedSubs = sortedSubs.filter((s) => s.status === 'paused');
  const totalMRR = payingSubs.reduce((sum, s) => sum + s.mrr, 0);

  return (
    <div className="arr-dashboard">
      {/* Header */}
      <div className="arr-header">
        <div>
          <h1>Newton ARR</h1>
          <span className="arr-refresh">
            Last refreshed: {current?.last_refresh || '—'}
          </span>
        </div>
        <div className="arr-headline">
          <div className="arr-headline-number">{fmt(current?.arr || 0)}</div>
          {current?.pending_churn_count > 0 && (
            <div className="arr-headline-trials" style={{ color: '#ef4444' }}>
              {fmt(current.pending_churn_arr)} pending churn
            </div>
          )}
          {current?.trial_count > 0 && (
            <div className="arr-headline-trials" style={{ color: '#3b82f6' }}>
              {fmt(current.trial_arr)} pending ARR from trials
            </div>
          )}
        </div>
      </div>

      {/* Pending ARR Changes */}
      {(subscriptions.some((s) => s.status === 'canceled') || subscriptions.some((s) => s.status === 'trialing')) && (() => {
        const churnSubs = subscriptions.filter((s) => s.status === 'canceled');
        const trialingSubs = subscriptions.filter((s) => s.status === 'trialing');
        const totalChurn = churnSubs.reduce((s, x) => s + x.arr, 0);
        const totalTrial = trialingSubs.reduce((s, x) => s + x.arr, 0);
        const net = totalTrial - totalChurn;
        const netCls = net > 0 ? 'positive' : net < 0 ? 'negative' : '';
        return (
          <div className="arr-card" style={{ padding: '12px 16px', fontSize: 13 }}>
            <h2 style={{ fontSize: 14, marginBottom: 6 }}>Pending ARR Changes</h2>
            <table className="arr-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th className="right">Type</th>
                  <th className="right">ARR Impact</th>
                  <th className="right">Date</th>
                </tr>
              </thead>
              <tbody>
                {trialingSubs.map((s) => (
                  <tr key={s.sub_id}>
                    <td>{s.customer_name}</td>
                    <td className="right" style={{ color: '#3b82f6' }}>Trial</td>
                    <td className="right mono positive">+{fmt(s.arr)}</td>
                    <td className="right">{s.billing_starts || '—'}</td>
                  </tr>
                ))}
                {churnSubs.map((s) => (
                  <tr key={s.sub_id}>
                    <td>{s.customer_name}</td>
                    <td className="right" style={{ color: '#ef4444' }}>Churn</td>
                    <td className="right mono negative">-{fmt(s.arr)}</td>
                    <td className="right">{s.churn_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                  <td colSpan={2} style={{ fontWeight: 600 }}>Net ARR Impact</td>
                  <td className={`right mono ${netCls}`} style={{ fontWeight: 600 }}>
                    {fmtChange(net)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })()}

      {/* View Toggle */}
      <div className="arr-view-toggle">
        <button
          className={`arr-view-btn ${view === 'overview' ? 'arr-view-active' : ''}`}
          onClick={() => setView('overview')}
        >
          Overview
        </button>
        <button
          className={`arr-view-btn ${view === 'subscriptions' ? 'arr-view-active' : ''}`}
          onClick={() => setView('subscriptions')}
        >
          Subscriptions
        </button>
      </div>

      {view === 'overview' && (
        <>
          {/* Chart */}
          <div className="arr-card">
            <h2>Monthly ARR</h2>
            <div className="arr-chart-container">
              <canvas ref={chartRef}></canvas>
            </div>
          </div>

          {/* MoM Table */}
          <div className="arr-card">
            <h2>Month over Month</h2>
            <div className="arr-table-wrap">
              <table className="arr-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="right">ARR</th>
                    <th className="right">MoM Growth</th>
                    <th className="right">New ARR</th>
                    <th className="right">Churned ARR</th>
                    <th className="right">Net ARR Change</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => {
                    const monthKey = m.date.slice(0, 7);
                    const churn = churnByMonth[monthKey];
                    const newArr = churn?.new_arr || 0;
                    const churnArr = churn?.churn_arr || 0;
                    const newDeals = churn?.new || [];
                    const churnDeals = churn?.churn || [];
                    const netChange = m.change || 0;
                    const netCls = netChange > 0 ? 'positive' : netChange < 0 ? 'negative' : '';
                    const pct = m.change_pct || 0;
                    const pctCls = pct > 0 ? 'positive' : pct < 0 ? 'negative' : '';
                    const isExpanded = expandedMonth === monthKey;
                    const hasDetail = newDeals.length > 0 || churnDeals.length > 0;
                    return (
                      <React.Fragment key={m.date}>
                        <tr
                          className={m.partial ? 'partial-row' : ''}
                          style={{ cursor: hasDetail ? 'pointer' : 'default' }}
                          onClick={() => hasDetail && setExpandedMonth(isExpanded ? null : monthKey)}
                        >
                          <td className="month-cell">
                            {hasDetail && (
                              <span style={{ display: 'inline-block', width: 16, fontSize: 10, color: '#94a3b8' }}>
                                {isExpanded ? '▼' : '▶'}
                              </span>
                            )}
                            {monthLabel(m.date, m.partial)}
                            {m.partial && <span className="mtd-badge">MTD</span>}
                          </td>
                          <td className="right mono">{fmt(m.arr)}</td>
                          <td className={`right mono ${pctCls}`}>
                            {pct ? fmtPct(pct) : '—'}
                          </td>
                          <td className="right mono positive">
                            {newArr ? '+' + fmt(newArr) : '—'}
                          </td>
                          <td className="right mono negative">
                            {churnArr ? '-' + fmt(churnArr) : '—'}
                          </td>
                          <td className={`right mono ${netCls}`}>
                            {(newArr || churnArr) ? fmtChange(netChange) : '—'}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} style={{ padding: '8px 16px 16px', background: '#f8fafc' }}>
                              <div style={{ display: 'flex', gap: 32 }}>
                                {newDeals.length > 0 && (
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, color: '#059669', fontSize: 13, marginBottom: 8 }}>
                                      New ({newDeals.length})
                                    </div>
                                    {newDeals.map((d) => (
                                      <div key={d.sub_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}>
                                        <span>{d.customer_name}</span>
                                        <span className="mono positive">+{fmt(d.arr)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {churnDeals.length > 0 && (
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, color: '#ef4444', fontSize: 13, marginBottom: 8 }}>
                                      Churned ({churnDeals.length})
                                    </div>
                                    {churnDeals.map((d) => (
                                      <div key={d.sub_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}>
                                        <span>{d.customer_name}{d.reason ? ` (${d.reason})` : ''}</span>
                                        <span className="mono negative">-{fmt(d.arr)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </>
      )}

      {view === 'subscriptions' && (
        <>
          {/* Paying Subscriptions */}
          <div className="arr-card">
            <div className="subs-header">
              <h2>Active Subscriptions ({payingSubs.length})</h2>
              <span className="subs-total">Total MRR: {fmt(totalMRR)}</span>
            </div>
            <div className="arr-table-wrap">
              <table className="arr-table subs-table">
                <thead>
                  <tr>
                    <th
                      className="sortable"
                      onClick={() => handleSort('customer_name')}
                    >
                      Customer {sortField === 'customer_name' && (sortDir === 'asc' ? '\u25B2' : '\u25BC')}
                    </th>
                    <th
                      className="right sortable"
                      onClick={() => handleSort('mrr')}
                    >
                      MRR {sortField === 'mrr' && (sortDir === 'asc' ? '\u25B2' : '\u25BC')}
                    </th>
                    <th
                      className="right sortable"
                      onClick={() => handleSort('arr')}
                    >
                      ARR {sortField === 'arr' && (sortDir === 'asc' ? '\u25B2' : '\u25BC')}
                    </th>
                    <th className="right">Latest Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {payingSubs.map((s) => (
                    <tr key={s.sub_id}>
                      <td className="subs-name">{s.customer_name}</td>
                      <td className="right mono">{fmt(s.mrr)}</td>
                      <td className="right mono">{fmt(s.arr)}</td>
                      <td className="right">
                        {invoiceBadge(s)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Trial Subscriptions */}
          {trialSubs.length > 0 && (
            <div className="arr-card">
              <h2 className="trials-heading">
                Trials ({trialSubs.length})
                <span className="trial-note"> — not included in ARR</span>
              </h2>
              <div className="arr-table-wrap">
                <table className="arr-table subs-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th className="right">MRR (when active)</th>
                      <th className="right">Billing Starts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trialSubs.map((s) => (
                      <tr key={s.sub_id} className="trial-row">
                        <td className="subs-name">{s.customer_name}</td>
                        <td className="right mono trials-col">{fmt(s.mrr)}</td>
                        <td className="right trials-col">{s.billing_starts || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Paused Subscriptions */}
          {pausedSubs.length > 0 && (
            <div className="arr-card">
              <h2 className="trials-heading">
                Paused ({pausedSubs.length})
                <span className="trial-note"> — not included in ARR</span>
              </h2>
              <div className="arr-table-wrap">
                <table className="arr-table subs-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th className="right">MRR (if reactivated)</th>
                      <th className="right">ARR (if reactivated)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pausedSubs.map((s) => (
                      <tr key={s.sub_id} className="trial-row">
                        <td className="subs-name">{s.customer_name}</td>
                        <td className="right mono trials-col">{fmt(s.mrr)}</td>
                        <td className="right mono trials-col">{fmt(s.arr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}

